import { useMemo } from 'react';

import {
	useGenerateAICampaign,
	useApproveEmail,
	useApproveAllEmails,
	useUpdateGeneratedEmail,
	useRegenerateEmail,
	useCancelAICampaignGeneration,
} from '../../../../models/aiCampaigns/mutations';
import { useGenerationStatus, useGeneratedEmailsWithRealtime } from '../../../../models/aiCampaigns/queries';

export const useEmailGeneration = (campaignId) => {
	// Queries
	const { data: statusData, isLoading: isLoadingStatus } = useGenerationStatus(campaignId);

	// Derived state for isGenerating (needed before emailsData query)
	const isGenerating = statusData?.status === 'generating';

	// Use realtime hook with infinite scroll pagination
	const {
		data: emailsData,
		isLoading: isLoadingEmails,
		refetch: refetchEmails,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = useGeneratedEmailsWithRealtime(campaignId, isGenerating);

	// Flatten all pages into a single emails array
	const emails = useMemo(() => {
		if (!emailsData?.pages) return [];
		return emailsData.pages.flatMap((page) => page.emails);
	}, [emailsData]);

	// Mutations
	const generateMutation = useGenerateAICampaign(campaignId);
	const approveEmailMutation = useApproveEmail(campaignId);
	const approveAllMutation = useApproveAllEmails(campaignId);
	const updateEmailMutation = useUpdateGeneratedEmail(campaignId);
	const regenerateMutation = useRegenerateEmail(campaignId);
	const cancelMutation = useCancelAICampaignGeneration(campaignId);

	// Derived state
	const isLoading = isLoadingStatus || isLoadingEmails;
	const progress = statusData?.jobStatus?.progress || 0;
	const stats = statusData?.stats || { total: 0, approved: 0, pending: 0 };
	const hasEmails = stats.total > 0;

	const generatedCount = stats.total;

	return {
		// Data
		emails,
		stats,
		progress,
		generatedCount,
		hasEmails,
		isGenerating,

		// Pagination
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,

		// Loading state
		isLoading,

		// Mutations
		generateMutation,
		approveEmailMutation,
		approveAllMutation,
		updateEmailMutation,
		regenerateMutation,
		cancelMutation,

		// Actions
		refetchEmails,
	};
};
